import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import { cn } from '@/lib/utils'
import { FormikProps } from 'formik'
import type { DynamicFormField } from '@/store/chat.store'
import { useTranslations } from 'next-intl'

interface DynamicFieldGeneratorProps {
  field: DynamicFormField
  formik: FormikProps<any>
  onFileChange?: (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement>) => void
  disabled?: boolean
}

const DynamicFieldGenerator = ({ field, formik, onFileChange, disabled = false }: DynamicFieldGeneratorProps) => {
  const t = useTranslations('dynamicForm')

  const renderField = () => {
    switch (field.type) {
      case 'date':
        return (
          <DatePicker
            value={formik.values[field.name] ? new Date(formik.values[field.name]) : undefined}
            onChange={date => {
              formik.setFieldValue(field.name, date ? date.toISOString().split('T')[0] : '')
            }}
            disabled={disabled}
            placeholder={field.placeholder}
            className={cn('!text-xs', formik.touched[field.name] && formik.errors[field.name] && 'border-destructive')}
          />
        )

      case 'file':
        // Native file inputs ignore the placeholder attribute, so render it as a visible label
        return (
          <label className='flex flex-col gap-1'>
            {field.placeholder && (
              <span className='text-xs font-medium'>
                {field.placeholder}
                {field.required && <span className='text-destructive'> *</span>}
              </span>
            )}
            <Input
              name={field.name}
              type='file'
              onChange={onFileChange?.(field.name)}
              onBlur={formik.handleBlur}
              disabled={disabled}
              className={cn(
                '!w-full !text-xs',
                formik.touched[field.name] && formik.errors[field.name] && 'border-destructive'
              )}
              accept='image/*,application/pdf'
              multiple
            />
          </label>
        )

      case 'select':
        return (
          <Select
            name={field.name}
            value={formik.values[field.name] || undefined}
            onValueChange={value => {
              formik.setFieldValue(field.name, value)
              formik.setFieldTouched(field.name, true, false)
            }}
            disabled={disabled}
          >
            <SelectTrigger
              onBlur={() => formik.setFieldTouched(field.name, true)}
              aria-invalid={Boolean(formik.touched[field.name] && formik.errors[field.name])}
              className={cn(
                '!text-xs',
                formik.touched[field.name] && formik.errors[field.name] && 'border-destructive'
              )}
            >
              <SelectValue placeholder={field.placeholder || t('selectOption')} />
            </SelectTrigger>
            <SelectContent className='z-[70]'>
              {field.options?.map(option => (
                <SelectItem key={option.value} value={option.value} className='text-xs'>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      default:
        return (
          <Input
            name={field.name}
            type={field.type}
            placeholder={field.placeholder}
            {...formik.getFieldProps(field.name)}
            disabled={disabled}
            className={cn(
              '!w-full !text-xs',
              formik.touched[field.name] && formik.errors[field.name] && 'border-destructive'
            )}
          />
        )
    }
  }

  return (
    <div className='flex flex-col gap-1'>
      {renderField()}
      {formik.touched[field.name] && formik.errors[field.name] && (
        <div className='text-destructive text-xs'>{formik.errors[field.name] as string}</div>
      )}
    </div>
  )
}

export default DynamicFieldGenerator
